import { SkyDialog, SkyMessageBar } from "@skypointcloud/skypoint-ui";
import { ROLE_LABELS } from "@shared/permissions";
import type { AccessRow } from "@/hooks/use-access";
import { INVITE_WAITING_LINE } from "@/components/access/UsersGrid";
import { usePermissions } from "@/hooks/use-permissions";
import { InlineErrorBar, type InlineFailure } from "@/lib/feedback/InlineErrorBar";

/**
 * What a confirm is about to put back (#2091): one revoked grant (instance tab, "Reactivate") or a
 * deactivated person's account (Platform tab, "Activate user"). Both restored access on one click
 * before, so a misclick gave a removed person access again.
 */
export type ReactivateTarget =
  { kind: "grant"; row: AccessRow; scopeName: string } | { kind: "account"; row: AccessRow };

/** The person as the dialog names them: their name when one is stored, and always the address. */
function personLabel(row: AccessRow): string {
  const name = row.displayName?.trim();
  return name && name !== row.email ? `${name} (${row.email})` : row.email;
}

/** An Email person whose sign-in account may not exist yet (the row's own "Waiting" facts). */
function waitsForAccount(row: AccessRow): boolean {
  return row.signInMethod === "email" && row.inviteWaiting === true;
}

/** What returns with one grant, and whether their sign-in returns with it. */
function grantDescription(row: AccessRow, scopeName: string, mayActivate: boolean): string {
  const label = ROLE_LABELS[row.roleName];
  const back = row.isSelf
    ? `You get the ${label} role back in ${scopeName}.`
    : `They get the ${label} role back in ${scopeName}.`;
  return `${back}${grantSignInLine(row, mayActivate)} The reactivation is recorded in the audit trail.`;
}

/** Whether the person's sign-in comes back with the grant, by their account's state. */
function grantSignInLine(row: AccessRow, mayActivate: boolean): string {
  if (row.userStatus === "disabled") {
    return mayActivate
      ? " Their account is deactivated, so they still cannot sign in until the account is activated. Activate it from the Platform tab."
      : " Their account is deactivated, so they still cannot sign in until a platform admin activates it.";
  }
  if (row.userStatus === "pending") {
    return waitsForAccount(row)
      ? ` ${INVITE_WAITING_LINE}`
      : " They get it once they sign in for the first time.";
  }
  return row.isSelf
    ? " It works at your next request."
    : " They can sign in and use it at their next request.";
}

/** The dialog's title for what is about to come back. */
function titleOf(target: ReactivateTarget | null): string {
  if (!target) return "Reactivate access";
  if (target.kind === "account") return `Activate ${personLabel(target.row)}`;
  const label = ROLE_LABELS[target.row.roleName];
  return target.row.isSelf
    ? `Reactivate your own ${label} in ${target.scopeName}`
    : `Reactivate ${label} for ${personLabel(target.row)} in ${target.scopeName}`;
}

/** The dialog's body for what is about to come back. */
function descriptionOf(target: ReactivateTarget | null, mayActivate: boolean): string | undefined {
  if (!target) return undefined;
  if (target.kind === "account") return accountDescription(target.row);
  return grantDescription(target.row, target.scopeName, mayActivate);
}

/** The error bar's title: none for a retryable failure, else what did not happen. */
function errorTitleOf(error: InlineFailure | null, isAccount: boolean): string | undefined {
  if (error?.retryable) return undefined;
  return isAccount ? "The account was not activated" : "The access was not reactivated";
}

/**
 * What returns with an account. A person with no bound sign-in (never signed in, or their sign-in
 * was reset) goes back to Pending, as the server's `statusAfterActiveChange` decides.
 */
function accountDescription(row: AccessRow): string {
  const back = row.idpIssuer
    ? "They can sign in again, and the roles they still hold work again."
    : "Their account becomes Pending first sign-in. Their roles work once they sign in for the first time.";
  return `${back} Grants that are disabled stay disabled. The activation is recorded in the audit trail.`;
}

/**
 * Confirm before access comes back (#2091), in the pattern of `RevokeGrantDialog` and
 * `DeactivateUserDialog`: the title names the person, the body says what returns (the role in
 * this scope, or the account's sign-in and the roles it still holds), and the dialog stays open
 * until the write lands (the parent clears `target`), so a refusal is read inside it.
 */
export function ReactivateAccessDialog({
  target,
  isSubmitting,
  onConfirm,
  onOpenChange,
  error = null,
}: {
  target: ReactivateTarget | null;
  isSubmitting: boolean;
  onConfirm: (target: ReactivateTarget) => void;
  onOpenChange: (open: boolean) => void;
  error?: InlineFailure | null;
}) {
  const { can } = usePermissions();
  const isAccount = target?.kind === "account";
  const title = titleOf(target);
  const description = descriptionOf(target, can("platform.tenants.manage"));
  return (
    <SkyDialog
      open={target !== null}
      onOpenChange={onOpenChange}
      // Only a landed write closes it (the parent clears `target`).
      onBeforeClose={(reason) => reason !== "primaryAction"}
      size="medium"
      testId="reactivate-dialog"
      title={title}
      description={description}
      primaryAction={{
        label: isAccount ? "Activate user" : "Reactivate access",
        loading: isSubmitting,
        onClick: () => {
          if (target) onConfirm(target);
        },
      }}
      secondaryAction={{
        label: isAccount ? "Keep deactivated" : "Keep disabled",
        onClick: () => onOpenChange(false),
      }}
    >
      <div className="flex flex-col gap-3">
        <SkyMessageBar
          intent="info"
          layout="multiline"
          title={isAccount ? "This gives back access to the account" : "This gives back access"}
          message="Check that this person should have access again before you confirm."
        />
        <InlineErrorBar
          error={error}
          title={errorTitleOf(error, isAccount)}
          testId="reactivate-error"
        />
      </div>
    </SkyDialog>
  );
}
